// 通常入札の CSV 出力 (入札一覧の「CSV ダウンロード」)。 spec `bidding/spot-bidding.md` §2 機能詳細 9、
// `conventions.md`「画面からダウンロードする CSV」。
//
// 列は CSV インポート (`bidding/spot-bid-normal-csv-import.md` §2) の取込と同じ 11 列 (列名・順序・
// 値の表記) を先頭に並べ、 末尾にステータス列を 1 列足す。 末尾の列を削除すれば、 そのまま
// CSV インポートで再アップロードできる。 画面に出ている札は下書き・承認待ちを含めてすべて出す
// (確定していない札を落とさない) ので、 札の状態はステータス列で分かるようにする。
// 入札番号・約定結果・受渡契約コード・作成者など、 取込で受け付けない列は出さない。
//
// 価格列・JEPX自由記述欄・排出係数紐付けは、 取り込むと同じ札に戻るように出す
// (spec 機能詳細 9。 ブロック入札の CSV [[buildBlockBidCsvFiles]] と同じ出し方で、 [[bidCsvCells]] を共有):
//   - 価格: 手数料自動考慮が OFF なら入札価格、 ON なら backend が保存済みの入札価格から現在の手数料単価で
//     逆算した希望価格 ([[normalBidCsvPriceQueries]] で問い合わせる)。 成行の札は空欄 (価格種別に 成行 を出す)
//   - 排出係数紐付けありの札: 排出係数紐付けに `1`、 JEPX自由記述欄に判別コメントの英字 1 文字
//     (判別コメントの後ろに書いた自由記述は出さない)
//   - 発注主体区分が分からない札 (eBalance 以外から取り込んだ札・CSV で指定できない区分) は空欄
//
// 旧「通常入札履歴」タブの CSV は本ファイルの対象外 (旧履歴タブは廃止、 オペレーション履歴タブへ
// 統合する決定 C。 `bidding/spot-bidding.md`)。 履歴タブは約定価格・入札番号などを出す
// 照合用の出力で、 [[buildSpotBidCsv]] の列のまま変えない。
import i18next from 'i18next'

import { convertAreaLabel } from '~/models/area'
import {
  BID_CSV_STATUS_HEADER,
  SPOT_BID_CSV_ORDER_TYPE_VALUE,
  SPOT_BID_NORMAL_CSV_DATA_TYPE_NAME,
  SPOT_BID_NORMAL_CSV_HEADER_BY_LANG,
  bidCsvLangOf,
} from '~/models/bidCsvHeader'
import { formatDateDisplay } from '~/models/dateTime'
import {
  type BidCsvPriceQuery,
  bidCsvEmissionCells,
  bidCsvIssuerTypeValue,
  bidCsvPriceCell,
  bidCsvSideValue,
} from '~/pages/SpotBidding/models/bidCsvCells'
import type { BidCsvExportInputs } from '~/pages/SpotBidding/models/bidCsvExportGate'
import { csvCell, neutralizeFormula } from '~/pages/SpotBidding/models/csvCell'
import {
  type SpotBidRow,
  isMarketOrderRow,
} from '~/pages/SpotBidding/models/spotBidRow'
import {
  SPOT_BID_STATUS_CATEGORY_LABEL_KEY,
  type SpotBidStatusCategory,
} from '~/pages/SpotBidding/models/spotBidStatusCategory'
import { LICENSE_TYPE_BY_ISSUER } from '~/pages/SpotBidding/models/spotBidWrite'

/** 出力する 1 ファイル。 */
export type NormalBidCsvFile = {
  filename: string
  csv: string
}

/**
 * 価格列の出所 (`price`) と自社の事業者 ID (`retailerId`: 排出係数紐付けの判別コメント) は
 * [[BidCsvExportInputs]]。 [[useBidCsvExportContext]] が出せると判定した時の値をそのまま展開して渡す。
 */
export type BuildNormalBidCsvOptions = BidCsvExportInputs & {
  /** 受渡日 (= 画面で選択中の受渡日。 全行の受渡日列とファイル名に使う)。 */
  deliveryDate: Date
  /**
   * 行のステータス区分。 画面の一覧 (`SpotBidTable`) とステータスフィルタに渡しているのと
   * 同じ関数を渡す。 CSV の中で締切判定を組み直すと、 画面と別の時計・別の締切判定で
   * 区分が決まり、 締切をまたぐ間に画面と CSV が食い違う (AC-BSP-048)。
   */
  statusCategoryOf: (row: SpotBidRow) => SpotBidStatusCategory
}

/** 価格列の逆算に問い合わせる札 (指値の札だけ。 成行の札は価格を出さない)。 */
export const normalBidCsvPriceQueries = (
  rows: readonly SpotBidRow[],
): BidCsvPriceQuery[] =>
  rows
    .filter((row) => !isMarketOrderRow(row))
    .map((row) => ({
      direction: row.direction,
      orderPriceYenPerKwh: row.priceYenPerKwh,
    }))

/**
 * 表示中の通常入札の札を、 取込フォーマット CSV (+ 末尾のステータス列) に整形する。
 * 表示順 (= 渡された rows の順) をそのまま使う。
 *
 * 表示言語が英語のときは、 ヘッダー行を英語ヘッダー名、 区分値 (発注主体区分・エリア・
 * 売買・価格種別・ステータス) とファイル名を英語で出す (spec §2「英語表示対応」、
 * conventions.md「CSV の英語表示」)。 取込は日本語・英語のどちらも受け付けるので、 どちらの
 * 言語で出しても取込にかけ直せる。 受渡日・コマ・数値は言語で変わらず、 自由記述の 2 列は訳さない。
 */
export const buildNormalBidCsv = (
  rows: readonly SpotBidRow[],
  options: BuildNormalBidCsvOptions,
): NormalBidCsvFile => {
  const { deliveryDate, statusCategoryOf, price, retailerId } = options
  // 取込は YYYY/MM/DD を保存時の形に揃えて受け付ける (spec §2 の受渡日)。
  const date = formatDateDisplay(deliveryDate)
  // エリア名を引く convertAreaLabel とステータス名を引く i18next が表示言語に従うので、
  // ヘッダー行と他の区分値も同じ言語で決める (1 つのファイルに日本語と英語を混ぜない)。
  const lang = bidCsvLangOf(i18next.language.startsWith('en'))

  const header = [
    ...SPOT_BID_NORMAL_CSV_HEADER_BY_LANG[lang],
    BID_CSV_STATUS_HEADER[lang],
  ]
  const body = rows.map((row) => {
    const isMarket = isMarketOrderRow(row)
    const orderType = isMarket
      ? SPOT_BID_CSV_ORDER_TYPE_VALUE.market
      : SPOT_BID_CSV_ORDER_TYPE_VALUE.limit
    return [
      date,
      String(row.timeCode),
      // 発注主体区分が分からない札 (未設定・DR) は空欄 ([[bidCsvIssuerTypeValue]])。
      bidCsvIssuerTypeValue(
        row.issuerType === undefined
          ? undefined
          : LICENSE_TYPE_BY_ISSUER[row.issuerType],
      )?.[lang] ?? '',
      convertAreaLabel(row.areaCode),
      bidCsvSideValue(row.direction)[lang],
      orderType[lang],
      // 成行の札の価格は "0" を運ぶが、 指値 0 円ではない。 価格は空欄にして価格種別で成行を示す
      // (取込は成行の行の価格を空欄で受け付ける。 AC-SPOT-006)。
      isMarket ? '' : bidCsvPriceCell(row.priceYenPerKwh, row.direction, price),
      Number(row.volumeMwh).toFixed(1),
      ...bidCsvEmissionCells(row.memoJepx, retailerId),
      // メモは数式インジェクション対策をかける (CWE-1236。 取込はセルの値を変換しない)。
      neutralizeFormula(row.memoFree ?? ''),
      // ステータス列は入札一覧のステータス列と同じ区分名を、 同じ i18n key から引く
      // (AC-BSP-047。 表示言語が英語なら英語)。
      i18next.t(SPOT_BID_STATUS_CATEGORY_LABEL_KEY[statusCategoryOf(row)]),
    ]
      .map((cell) => csvCell(cell))
      .join(',')
  })

  // ファイル名は common.md §6 のデータ種別名に選択中の受渡日を付ける (入札一覧の CSV のファイル名は
  // 機能詳細 9 に定めが無く、 ブロック入札の CSV と同じ形にそろえる)。
  return {
    filename: `${SPOT_BID_NORMAL_CSV_DATA_TYPE_NAME[lang]}_${date.replaceAll('/', '')}.csv`,
    csv: [header.join(','), ...body].join('\n'),
  }
}
